import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const RANK = { explore: 0, essential: 1, complete: 2 };

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { api.get("/catalog").then(({data}) => setPlans(data.plans)); }, []);

  const buy = async (planCode) => {
    const packageId = `${planCode}_${annual ? "annual" : "monthly"}`;
    if (!user) { nav(`/register?next=${encodeURIComponent(`/pricing?plan=${packageId}`)}`); return; }
    setBusy(planCode);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: packageId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); setBusy(null); }
  };
  return (
    <div>
      <section className="rle-gradient-bg py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <span className="rle-eyebrow">Simple, honest pricing</span>
          <h1 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl text-slate-900">Choose the plan that fits your life.</h1>
          <p className="mt-4 text-slate-600">Start free. Upgrade when you're ready. Cancel anytime.</p>
          <div className="mt-8 inline-flex items-center rounded-full bg-white border border-slate-200 p-1">
            <button onClick={() => setAnnual(false)} className={`px-4 py-2 rounded-full text-sm font-semibold ${!annual ? "bg-emerald-700 text-white" : "text-slate-700"}`} data-testid="pricing-monthly-toggle">Monthly</button>
            <button onClick={() => setAnnual(true)} className={`px-4 py-2 rounded-full text-sm font-semibold ${annual ? "bg-emerald-700 text-white" : "text-slate-700"}`} data-testid="pricing-annual-toggle">Annual · save</button>
          </div>
        </div>
      </section>
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div key={p.code} className={`rle-card p-6 ${p.highlight ? "ring-2 ring-emerald-700" : ""}`} data-testid={`pricing-plan-${p.code}`}>
              {p.highlight && <div className="text-xs font-semibold text-emerald-700 mb-2 tracking-widest">MOST FEATURES</div>}
              <div className="font-display font-bold text-2xl text-slate-900">{p.name}</div>
              <p className="text-sm text-slate-600 mt-1">{p.positioning}</p>
              <div className="mt-4">
                <span className="text-4xl font-display font-extrabold text-slate-900">
                  A${(annual ? p.price_annual : p.price_monthly).toFixed(2)}
                </span>
                <span className="text-sm text-slate-500 ml-1">{p.price_monthly === 0 ? "" : annual ? "/year" : "/month"}</span>
              </div>
              <ul className="mt-6 space-y-2 text-sm text-slate-700">
                {p.features.map((f, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-700 mt-0.5 shrink-0"/>{f}</li>)}
              </ul>
              <div className="mt-6">
                {p.code === "explore" ? (
                  <Link to={user ? "/dashboard" : "/register"}><Button variant="outline" className="w-full rounded-full" data-testid={`pricing-cta-${p.code}-btn`}>{user ? "Go to Dashboard" : "Get Started Free"}</Button></Link>
                ) : user && user.plan === p.code ? (
                  <Button disabled variant="outline" className="w-full rounded-full" data-testid={`pricing-cta-${p.code}-btn`}>Your current plan</Button>
                ) : (
                  <Button onClick={() => buy(p.code)} disabled={!!busy} className={`w-full rounded-full ${p.highlight ? "bg-emerald-700 hover:bg-emerald-800" : ""}`} data-testid={`pricing-cta-${p.code}-btn`}>
                    {busy === p.code ? <Loader2 size={16} className="mr-1 animate-spin"/> : null}
                    {busy === p.code ? "Opening secure checkout…" : user && RANK[user.plan] > RANK[p.code] ? `Switch to ${p.name}` : user ? `Upgrade to ${p.name}` : `Choose ${p.name}`}
                  </Button>
                )}
              </div>
              {p.code !== "explore" && (
                <p className="text-xs text-slate-500 mt-3 text-center">Secure card payment via Stripe. Renews {annual ? "yearly" : "monthly"}, cancel anytime.</p>
              )}
            </div>
          ))}
        </div>
        <p className="max-w-4xl mx-auto text-xs text-slate-500 text-center mt-10 px-4">
          We do not guarantee any language-proficiency outcome, IELTS/PTE/CLB score, employment or immigration result. This product is an educational language-learning and communication-practice platform.
        </p>
      </section>
    </div>
  );
}
